/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useContacts } from './useContacts';
import ContactCardView from './components/ContactCardView';
import ContactListView from './components/ContactListView';
import ViewToggle, { ViewType } from '../components/ViewToggle';
import SearchAndFilters from '../components/SearchAndFilters';
import ColumnFilters, { SortField, SortDirection } from '../components/ColumnFilters';
import EmailActions from './components/EmailActions';
import MeetingActions from './components/MeetingActions';
import SourceToggle from '../components/SourceToggle';
import { Settings } from 'lucide-react';
import SyncControls from '../components/SyncControls';
import SyncStatus from '../components/SyncStatus';
import CardCustomizer from '../components/CardCustomizer';
import { CardFieldType } from '../components/CardCustomizer';
import ListCustomizer from '../components/ListCustomizer';
import { ListFieldType } from '../components/ListCustomizer';

/* global Office */

const TaskpaneComponent: React.FC = () => {
  const [isOfficeReady, setIsOfficeReady] = useState(false);
  const [isMeeting, setIsMeeting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [currentView, setCurrentView] = useState<ViewType>(() => {
    // Prefer saved value from localStorage for instant hydration
    try {
      const saved = localStorage.getItem('ui.view');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Map legacy 'compact' to 'list'
        if (parsed === 'compact') return 'list';
        return parsed as ViewType;
      }
    } catch {}
    // Fallback: if screen width is less than 450px, start with card view
    const width = document.documentElement.clientWidth || window.innerWidth;
    return width < 450 ? 'card' : 'list';
  })
  const [showSyncStatus, setShowSyncStatus] = useState(false);
  const [showCardCustomizer, setShowCardCustomizer] = useState(false);
  const [showListCustomizer, setShowListCustomizer] = useState(false);
  const [customFieldOrder, setCustomFieldOrder] = useState<CardFieldType[]>(() => {
    try {
      const saved = localStorage.getItem('card.customOrder');
      if (saved) return JSON.parse(saved) as CardFieldType[];
    } catch {}
    return ['name', 'title', 'company', 'email', 'phone', 'source'];
  });
  const [nameFormat, setNameFormat] = useState<string>(() => {
    try {
      const saved = localStorage.getItem('card.lastFirstFormat');
      return saved === 'true' ? 'lastFirst' : 'firstLast';
    } catch {}
    return 'firstLast';
  });
  const [selectedCount, setSelectedCount] = useState(0);
  const [listFieldOrder, setListFieldOrder] = useState<ListFieldType[]>(() => {
    try {
      const saved = localStorage.getItem('list.customOrder');
      if (saved) return JSON.parse(saved) as ListFieldType[];
    } catch {}
    return ['company', 'lastName', 'firstName', 'email', 'source', 'none'];
  });
  const [contactSource, setContactSource] = useState<'corporate' | 'personal' | 'internal' | 'all'>(() => {
    try {
      const saved = localStorage.getItem('ui.source');
      if (saved) return JSON.parse(saved) as 'corporate' | 'personal' | 'internal' | 'all';
    } catch {}
    return 'all';
  });
  const [filteredContacts, setFilteredContacts] = useState<any[]>([]);
  const [filteredSearchResults, setFilteredSearchResults] = useState<any[]>([]);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    company: '',
    lastName: '',
    firstName: '',
    email: ''
  });
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const progressBarRef = useRef<HTMLDivElement>(null);
  const fetchInProgressRef = useRef(false);
  
  const { isAuthenticated, isLoading: authLoading, error: authError, userInfo, authenticateUser, getTokenForContacts } = useAuth();
  const { 
    contacts, 
    searchResults,
    isLoading: contactsLoading, 
    isSyncing,
    error: contactsError, 
    syncProgress,
    syncStatus,
    contactStore,
    fetchContactsFromSharedMailbox,
    fetchPersonalContacts,
    fetchInternalUsers,
    getContactsBySourceType,
    searchContacts,
    clearSearch,
    forceResync,
    forceParallelResync,
    parallelStartupSync,
    forcePersonalResync,
    clearContacts,
    checkAutoSyncStatus,
    manualStartAutoSync,
    refreshUI,
    checkAndPerformStartupSync,
    checkAndPerformPersonalStartupSync,
    checkAndPerformInternalStartupSync
  } = useContacts();

  useEffect(() => {
    Office.onReady((info) => {
      if (info.host === Office.HostType.Outlook) {
        setIsOfficeReady(true);
        
        // Detect if this is a meeting or email
        if (Office.context.mailbox.item) {
          const itemType = Office.context.mailbox.item.itemType;
          setIsMeeting(itemType === Office.MailboxEnums.ItemType.Appointment);
        }
        
        // Automatically authenticate when Office is ready
        authenticateUser();
      }
    });

    // Cleanup function to clear email state when add-in is closed
    return () => {
      if (contactStore) {
        contactStore.clearEmailState();
      }
    };
  }, [authenticateUser, contactStore]);

  // Clear selection only when source changes (not when contacts update)
  const prevSourceRef = useRef<'corporate' | 'personal' | 'internal' | 'all'>();
  useEffect(() => {
    if (prevSourceRef.current !== undefined && prevSourceRef.current !== contactSource && contactStore) {
      contactStore.clearSelection();
    }
    prevSourceRef.current = contactSource;
  }, [contactSource, contactStore]);

  // Persist view selection
  useEffect(() => {
    try {
      localStorage.setItem('ui.view', JSON.stringify(currentView));
    } catch {}
  }, [currentView]);

  // Persist source selection
  useEffect(() => {
    try {
      localStorage.setItem('ui.source', JSON.stringify(contactSource));
    } catch {}
  }, [contactSource]);

  // Filter contacts based on selected source
  useEffect(() => {
    const filterContacts = async () => {
      if (!contactStore) return;
      
      try {
        if (contactSource === 'all') {
          setFilteredContacts(contacts);
        } else {
          const sourceContacts = await getContactsBySourceType(contactSource);
          setFilteredContacts(sourceContacts);
        }
      } catch (error) {
        console.error('Error filtering contacts:', error);
        setFilteredContacts(contacts);
      }
    };

    filterContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, contactSource, contactStore]);

  // Debug log filteredContacts changes (removed to prevent infinite loop)

  // Filter search results based on selected source
  useEffect(() => {
    const filterSearchResults = () => {
      if (contactSource === 'all') {
        setFilteredSearchResults(searchResults);
      } else {
        const filtered = searchResults.filter(result => 
          result.contact.sourceType === contactSource
        );
        setFilteredSearchResults(filtered);
      }
    };

    filterSearchResults();
  }, [searchResults, contactSource]);

  // Automatically fetch contacts when authenticated
  useEffect(() => {
    if (isAuthenticated && !contactsLoading && !isSyncing && !fetchInProgressRef.current && contacts.length === 0 && 
        (!syncStatus?.isBootstrapComplete || !syncStatus?.personalBootstrapComplete || !syncStatus?.internalBootstrapComplete)) {
      handleFetchContacts();
    }
  }, [isAuthenticated, contactsLoading, isSyncing, contacts.length, syncStatus?.isBootstrapComplete, syncStatus?.personalBootstrapComplete, syncStatus?.internalBootstrapComplete]);

  // Check for startup sync when authenticated
  useEffect(() => {
    const handleStartupSync = async () => {
      if (isAuthenticated && !isSyncing && !fetchInProgressRef.current) {
        // Prevent multiple simultaneous startup syncs
        if (fetchInProgressRef.current) {
          console.log('Startup sync already in progress, skipping...');
          return;
        }
        
        fetchInProgressRef.current = true;
        
        try {
          console.log('Add-in loaded, performing parallel startup sync...');
          const accessToken = await getTokenForContacts();
          
          // Get user email for parallel sync
          let userEmail = userInfo?.email;
          if (!userEmail) {
            // Try to get user email from Graph API
            try {
              const response = await fetch('https://graph.microsoft.com/v1.0/me', {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              });
              if (response.ok) {
                const userData = await response.json();
                userEmail = userData.mail || userData.userPrincipalName;
              }
            } catch (error) {
              console.warn('Could not fetch user email for parallel startup sync:', error);
            }
          }
          
          if (userEmail) {
            console.log('Starting parallel startup sync for all contact sources...');
            await parallelStartupSync(accessToken, userEmail);
            console.log('Parallel startup sync completed successfully');
          } else {
            console.warn('No user email available, falling back to sequential startup sync');
            // Fallback to sequential sync if no user email
            const corporateSyncPerformed = await checkAndPerformStartupSync(accessToken, getTokenForContacts);
            if (corporateSyncPerformed) {
              console.log('Corporate startup sync was performed');
            }
            
            const internalSyncPerformed = await checkAndPerformInternalStartupSync(accessToken, getTokenForContacts);
            if (internalSyncPerformed) {
              console.log('Internal startup sync was performed');
            }
          }
        } catch (err) {
          console.error('Error during parallel startup sync:', err);
        } finally {
          fetchInProgressRef.current = false;
        }
      }
    };

    handleStartupSync();
  }, [isAuthenticated]);

  const handleFetchContacts = async () => {
    if (fetchInProgressRef.current) {
      console.log('Fetch already in progress, skipping...');
      return;
    }
    
    fetchInProgressRef.current = true;
    try {
      const accessToken = await getTokenForContacts();
      
      // Get user email for parallel sync
      let userEmail = userInfo?.email;
      if (!userEmail) {
        // Try to get user email from the token or make a Graph API call
        try {
          const response = await fetch('https://graph.microsoft.com/v1.0/me', {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          if (response.ok) {
            const userData = await response.json();
            userEmail = userData.mail || userData.userPrincipalName;
          }
        } catch (error) {
          console.warn('Could not fetch user email for parallel contacts fetch:', error);
        }
      }
      
      if (userEmail) {
        console.log('Starting parallel contacts fetch for all sources...');
        await forceParallelResync(accessToken, userEmail);
        console.log('Parallel contacts fetch completed successfully');
      } else {
        console.warn('No user email available, falling back to sequential contacts fetch');
        // Fallback to sequential fetch if no user email
        await fetchContactsFromSharedMailbox(accessToken, getTokenForContacts);
        try {
          await fetchInternalUsers(accessToken, getTokenForContacts);
          console.log('Internal users fetch completed successfully');
        } catch (error) {
          console.error('Error fetching internal users:', error);
        }
      }
    } catch (err) {
      console.error('Error acquiring token for contacts:', err);
    } finally {
      fetchInProgressRef.current = false;
    }
  };

  const handleForceResync = async () => {
    try {
      const accessToken = await getTokenForContacts();
      
      // Get user email for parallel sync
      let userEmail = userInfo?.email;
      if (!userEmail) {
        // Try to get user email from the token or make a Graph API call
        try {
          const response = await fetch('https://graph.microsoft.com/v1.0/me', {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          if (response.ok) {
            const userData = await response.json();
            userEmail = userData.mail || userData.userPrincipalName;
          }
        } catch (error) {
          console.warn('Could not fetch user email for parallel sync:', error);
        }
      }
      
      if (userEmail) {
        console.log('Starting parallel resync for all contact sources...');
        await forceParallelResync(accessToken, userEmail);
        console.log('Parallel resync completed successfully');
      } else {
        console.warn('No user email available, falling back to sequential sync');
        // Fallback to sequential sync if no user email
        await forceResync(accessToken);
        
        // Also sync internal users
        console.log('Force resyncing internal users...');
        try {
          await fetchInternalUsers(accessToken, getTokenForContacts);
          console.log('Internal users force resync completed successfully');
        } catch (error) {
          console.error('Error force resyncing internal users:', error);
        }
      }
    } catch (err) {
      console.error('Error force resyncing contacts:', err);
    }
  };


  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (query.trim().length >= 2) {
      searchContacts(query);
      setShowSearchResults(true);
    } else {
      clearSearch();
      setShowSearchResults(false);
    }
  }, [searchContacts, clearSearch]);

  const handleColumnFilterChange = (column: keyof typeof columnFilters, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const handleClearFilters = () => {
    setColumnFilters({
      company: '',
      lastName: '',
      firstName: '',
      email: ''
    });
  };

  const handleSortChange = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleCardOrderChange = (order: CardFieldType[]) => {
    setCustomFieldOrder(order);
  };

  const handleNameFormatChange = (format: string) => {
    setNameFormat(format);
    try {
      localStorage.setItem('card.lastFirstFormat', format === 'lastFirst' ? 'true' : 'false');
    } catch (error) {
      console.error('Failed to save name format:', error);
    }
  };

  const addSelectedToEmail = async (field: 'to' | 'cc' | 'bcc') => {
    try {
      if (!contactStore) return;
      const selectedContacts = await contactStore.getSelectedContacts();
      if (selectedContacts.length === 0) return;

      const newEmails = selectedContacts
        .map(contact => contact.emailPrimary)
        .filter(email => email);

      if (newEmails.length > 0 && Office.context.mailbox.item) {
        // First get existing emails
        Office.context.mailbox.item[field].getAsync((existingResult) => {
          if (existingResult.status === Office.AsyncResultStatus.Succeeded) {
            const existingEmails = existingResult.value || [];
            const existingEmailStrings = existingEmails.map((email: any) => email.emailAddress || email);
            
            // Combine existing and new emails, removing duplicates
            const allEmails = [...existingEmailStrings, ...newEmails];
            const uniqueEmails = Array.from(new Set(allEmails));
            
            // Set the combined emails
            Office.context.mailbox.item![field].setAsync(uniqueEmails, (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                // Mark the selected contacts as being in email fields
                const selectedContactIds = selectedContacts.map(contact => contact.id);
                contactStore.markContactsInEmail(selectedContactIds);
                contactStore.clearSelection();
              } else {
                console.error('Failed to add contacts to email field:', result.error);
              }
            });
          } else {
            console.error('Failed to get existing emails:', existingResult.error);
          }
        });
      }
    } catch (error) {
      console.error('Error adding contacts to email:', error);
    }
  };

  const addSelectedToMeeting = async (field: 'required' | 'optional') => {
    try {
      if (!contactStore) return;
      const selectedContacts = await contactStore.getSelectedContacts();
      if (selectedContacts.length === 0) return;

      const newAttendees = selectedContacts
        .map(contact => ({
          emailAddress: contact.emailPrimary,
          displayName: `${contact.givenName || ''} ${contact.surname || ''}`.trim() || contact.emailPrimary
        }))
        .filter(attendee => attendee.emailAddress);

      if (newAttendees.length > 0 && Office.context.mailbox.item) {
        const attendeeField = field === 'required' ? 'requiredAttendees' : 'optionalAttendees';
        
        // First get existing attendees to check for duplicates
        Office.context.mailbox.item[attendeeField].getAsync((existingResult) => {
          if (existingResult.status === Office.AsyncResultStatus.Succeeded) {
            const existingAttendees = existingResult.value || [];
            const existingEmails = existingAttendees.map((attendee: any) => attendee.emailAddress);
            
            // Filter out duplicates from new attendees
            const uniqueNewAttendees = newAttendees.filter(
              attendee => !existingEmails.includes(attendee.emailAddress)
            );
            
            // Use addAsync to add only the new attendees (doesn't affect organizer or existing attendees)
            if (uniqueNewAttendees.length > 0) {
              Office.context.mailbox.item![attendeeField].addAsync(uniqueNewAttendees, (result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  // Mark the selected contacts as being in email fields
                  const selectedContactIds = selectedContacts.map(contact => contact.id);
                  contactStore.markContactsInEmail(selectedContactIds);
                  contactStore.clearSelection();
                } else {
                  console.error('Failed to add contacts to meeting field:', result.error);
                }
              });
            } else {
              // All contacts were duplicates, just clear selection
              contactStore.clearSelection();
            }
          } else {
            console.error('Failed to get existing attendees:', existingResult.error);
          }
        });
      }
    } catch (error) {
      console.error('Error adding contacts to meeting:', error);
    }
  };


  const checkExistingEmailsInFields = async () => {
    if (!contactStore || !Office.context.mailbox.item) return;

    try {
      const allEmails: string[] = [];
      
      if (isMeeting) {
        // For meetings, check requiredAttendees and optionalAttendees
        const attendeeFields: ('requiredAttendees' | 'optionalAttendees')[] = ['requiredAttendees', 'optionalAttendees'];
        
        for (const field of attendeeFields) {
          await new Promise<void>((resolve) => {
            Office.context.mailbox.item![field].getAsync((result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                const attendees = result.value || [];
                const emailStrings = attendees.map((attendee: any) => attendee.emailAddress);
                allEmails.push(...emailStrings);
              }
              resolve();
            });
          });
        }
      } else {
        // For emails, check to, cc, bcc fields
        const fields: ('to' | 'cc' | 'bcc')[] = ['to', 'cc', 'bcc'];
        
        for (const field of fields) {
          await new Promise<void>((resolve) => {
            Office.context.mailbox.item![field].getAsync((result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                const emails = result.value || [];
                const emailStrings = emails.map((email: any) => email.emailAddress || email);
                allEmails.push(...emailStrings);
              }
              resolve();
            });
          });
        }
      }

      // Find contacts that match these email addresses
      if (allEmails.length > 0) {
        const matchingContacts = await contactStore.findContactsByEmails(allEmails);
        if (matchingContacts.length > 0) {
          const matchingContactIds = matchingContacts.map(contact => contact.id);
          contactStore.markContactsInEmail(matchingContactIds);
        }
      }
    } catch (error) {
      console.error('Error checking existing emails in fields:', error);
    }
  };

  // Update progress bar width when sync progress changes
  useEffect(() => {
    if (syncProgress && progressBarRef.current) {
      const width = syncProgress.total > 0 ? (syncProgress.current / syncProgress.total) * 100 : 0;
      progressBarRef.current.style.width = `${width}%`;
    }
  }, [syncProgress]);

  // Track selected contacts count
  useEffect(() => {
    if (!contactStore) return;

    const updateSelectionCount = () => {
      setSelectedCount(contactStore.getSelectedContactsCount());
    };

    contactStore.addSelectionListener(updateSelectionCount);
    updateSelectionCount();

    return () => {
      contactStore.removeSelectionListener(updateSelectionCount);
    };
  }, [contactStore]);

  // Clear email state when component unmounts
  useEffect(() => {
    return () => {
      if (contactStore) {
        contactStore.clearEmailState();
      }
    };
  }, [contactStore]);

  // Check existing emails in fields when contacts are loaded
  useEffect(() => {
    if (isOfficeReady && contactStore && contacts.length > 0 && !contactsLoading) {
      checkExistingEmailsInFields();
    }
  }, [isOfficeReady, contactStore, contacts.length, contactsLoading, isMeeting]);

  return (
    <div className="h-screen w-full font-sans flex flex-col">
      {/* <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center space-x-4">
            <img 
              width="90" 
              height="90" 
              src="../../assets/logo-filled.png" 
              alt="CContacts" 
              title="CContacts"
              className="rounded-lg"
            />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Welcome to CContacts</h1>
              <p className="text-gray-600">Contact management made simple</p>
            </div>
          </div>
        </div>
      </header> */}

      <main className="flex-1 flex flex-col w-full rounded-lg overflow-hidden">
        {!isOfficeReady ? (
          <section className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Please{' '}
              <a 
                target="_blank" 
                rel="noopener noreferrer" 
                href="https://learn.microsoft.com/office/dev/add-ins/testing/test-debug-office-add-ins#sideload-an-office-add-in-for-testing"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                sideload
              </a>{' '}
              your add-in to see the app.
            </h2>
          </section>
        ) : (
          <div className="w-full flex flex-col h-full">
            {/* Loading State */}
            {(authLoading || contactsLoading) && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-600">
                  {authLoading ? 'Authenticating...' : 'Loading contacts...'}
                </p>
              </div>
            )}

            {/* Sync Progress */}
            {syncProgress && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-blue-900">
                    {syncProgress.phase === 'bootstrap' ? 'Initial Sync' : 
                     syncProgress.phase === 'incremental' ? 'Updating Contacts' :
                     syncProgress.phase === 'search-index' ? 'Building Search Index' :
                     syncProgress.phase === 'complete' ? 'Sync Complete' : 'Syncing'}
                  </h4>
                  <span className="text-sm text-blue-700">
                    {syncProgress.current}/{syncProgress.total}
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                  <div 
                    ref={progressBarRef}
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  ></div>
                </div>
                <p className="text-sm text-blue-700">{syncProgress.message}</p>
              </div>
            )}

            {/* Error State */}
            {(authError || contactsError) && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800">{authError || contactsError}</p>
              </div>
            )}

            {/* Contacts Section */}
            {isAuthenticated && !authLoading && (
              <section className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col flex-1 min-h-0 w-full">
                {/* Fixed Header Controls */}
                <div className="p-3 sm:p-4 lg:p-6 pb-3 sm:pb-4 border-b border-gray-200 bg-white">
                  {/* Top Row - Source and View Controls */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <SourceToggle value={contactSource} onChange={setContactSource} />
                    <div className="flex items-center gap-2">
                      <ViewToggle currentView={currentView} onViewChange={setCurrentView} />
                      {currentView === 'card' && (
                        <button
                          onClick={() => setShowCardCustomizer(!showCardCustomizer)}
                          className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 hover:shadow-md ${
                            showCardCustomizer
                              ? 'bg-blue-100 text-blue-800 border border-blue-300'
                              : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                          }`}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <Settings className="w-4 h-4" />
                            <span>Card</span>
                          </span>
                        </button>
                      )}
                      {currentView === 'list' && (
                        <button
                          onClick={() => setShowListCustomizer(!showListCustomizer)}
                          className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 hover:shadow-md ${
                            showListCustomizer
                              ? 'bg-blue-100 text-blue-800 border border-blue-300'
                              : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                          }`}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <Settings className="w-4 h-4" />
                            <span>List</span>
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                  </div>

                  {/* Sync Controls */}
                  <div className="relative">
                    {showCardCustomizer && (
                      <CardCustomizer
                        onClose={() => setShowCardCustomizer(false)}
                        onChange={handleCardOrderChange}
                        onNameFormatChange={handleNameFormatChange}
                      />
                    )}
                    {showListCustomizer && (
                      <ListCustomizer
                        onClose={() => setShowListCustomizer(false)}
                        onChange={(order) => setListFieldOrder(order)}
                        defaultOrder={['company', 'lastName', 'firstName', 'email', 'source', 'none']}
                      />
                    )}
                    {syncStatus && showSyncStatus && (
                      <SyncStatus
                        syncStatus={syncStatus}
                        sharedUpn={process.env.SHARED_UPN}
                        userEmail={userInfo?.email}
                        isSyncing={isSyncing}
                        onClose={() => setShowSyncStatus(false)}
                      />
                    )}
                    <SyncControls
                      showSyncStatus={showSyncStatus}
                      onToggleSyncStatus={() => setShowSyncStatus(!showSyncStatus)}
                      onForceResync={handleForceResync}
                      isLoading={contactsLoading}
                      isSyncing={isSyncing}
                      currentView={currentView}
                      onToggleCardCustomizer={() => setShowCardCustomizer(!showCardCustomizer)}
                      showCardCustomizer={showCardCustomizer}
                      onToggleListCustomizer={() => setShowListCustomizer(!showListCustomizer)}
                      showListCustomizer={showListCustomizer}
                    />
                  </div>
         

                  {/* Search and Filter Toggle */}
                  <SearchAndFilters
                    searchQuery={searchQuery}
                    onSearchChange={handleSearch}
                    showColumnFilters={showColumnFilters}
                    onToggleFilters={() => setShowColumnFilters(!showColumnFilters)}
                    placeholder="Search contacts..."
                  />

                  {/* Column Filters */}
                  <ColumnFilters
                    showFilters={showColumnFilters}
                    columnFilters={columnFilters}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onFilterChange={handleColumnFilterChange}
                    onSortChange={handleSortChange}
                    onClearFilters={handleClearFilters}
                    onCloseFilters={() => setShowColumnFilters(false)}
                  />

                  {/* Search Results Indicator */}
                  {showSearchResults && contactSource !== 'all' && (
                    <div className="flex justify-center">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">Filtering by:</span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          contactSource === 'personal' 
                            ? 'bg-green-100 text-green-800' 
                            : contactSource === 'internal'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {contactSource === 'personal' ? 'Personal' : contactSource === 'internal' ? 'Internal' : 'Corporate'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Email/Meeting Actions */}
                  {isMeeting ? (
                    <MeetingActions
                      selectedCount={selectedCount}
                      onAddToMeeting={addSelectedToMeeting}
                    />
                  ) : (
                    <EmailActions
                      selectedCount={selectedCount}
                      onAddToEmail={addSelectedToEmail}
                    />
                  )}
                </div>

                {/* Scrollable Content Area */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 pt-3 w-full">

                  {/* Search Results */}
                  {showSearchResults && filteredSearchResults.length > 0 && contactStore && (
                    currentView === 'card' ? (
                      <ContactCardView 
                        key={`search-card-${customFieldOrder.join('-')}-${nameFormat}`}
                        contacts={filteredSearchResults.map(result => result.contact)} 
                        title={`Search Results (${contactSource === 'all' ? 'All' : contactSource === 'corporate' ? 'Corporate' : contactSource === 'personal' ? 'Personal' : 'Internal'})`}
                        maxHeight="max-h-none"
                        contactStore={contactStore}
                        columnFilters={columnFilters}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        customFieldOrder={customFieldOrder}
                        nameFormat={nameFormat}
                      />
                    ) : currentView === 'list' ? (
                      <ContactListView 
                        contacts={filteredSearchResults.map(result => result.contact)} 
                        title={`Search Results (${contactSource === 'all' ? 'All' : contactSource === 'corporate' ? 'Corporate' : contactSource === 'personal' ? 'Personal' : 'Internal'})`}
                        maxHeight="max-h-none"
                        contactStore={contactStore}
                        columnFilters={columnFilters}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        customFieldOrder={listFieldOrder}
                      />
                    ) : null
                  )}

                  {/* Filtered Contacts */}
                  {!showSearchResults && filteredContacts.length > 0 && contactStore && (
                    currentView === 'card' ? (
                      <ContactCardView 
                        key={`${contactSource}-card-${customFieldOrder.join('-')}-${nameFormat}`}
                        contacts={filteredContacts} 
                        title={`${contactSource === 'all' ? 'All' : contactSource === 'corporate' ? 'Corporate' : contactSource === 'personal' ? 'Personal' : 'Internal'} Contacts`}
                        maxHeight="max-h-none"
                        contactStore={contactStore}
                        columnFilters={columnFilters}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        customFieldOrder={customFieldOrder}
                        nameFormat={nameFormat}
                      />
                    ) : currentView === 'list' ? (
                      <ContactListView 
                        key={`${contactSource}-list-${listFieldOrder.join('-')}`}
                        contacts={filteredContacts} 
                        title={`${contactSource === 'all' ? 'All' : contactSource === 'corporate' ? 'Corporate' : contactSource === 'personal' ? 'Personal' : 'Internal'} Contacts`}
                        maxHeight="max-h-none"
                        contactStore={contactStore}
                        columnFilters={columnFilters}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        customFieldOrder={listFieldOrder}
                      />
                    ) : null
                  )}

                  {filteredContacts.length === 0 && !contactsLoading && !showSearchResults && (
                    <div className="text-center py-8">
                      <p className="text-gray-600">
                        {contactSource === 'all' 
                          ? 'No contacts found. Click "Sync Now" to load contacts from all sources.'
                          : contactSource === 'corporate'
                          ? 'No corporate contacts found. Click "Sync Now" to load contacts from the shared mailbox.'
                          : contactSource === 'personal'
                          ? 'No personal contacts found. Click "Sync Now" to load your personal contacts.'
                          : 'No internal users found. Click "Sync Now" to load organization members.'
                        }
                      </p>
                    </div>
                  )}

                  {showSearchResults && filteredSearchResults.length === 0 && searchQuery.trim().length >= 2 && (
                    <div className="text-center py-8">
                      <p className="text-gray-600">
                        {searchResults.length === 0 
                          ? `No contacts found matching "${searchQuery}".`
                          : `No ${contactSource === 'all' ? '' : contactSource === 'corporate' ? 'corporate ' : contactSource === 'personal' ? 'personal ' : 'internal '}contacts found matching "${searchQuery}".`
                        }
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default TaskpaneComponent;